import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { AlertCircle, X, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBanner = () => {
  const { user } = useAuth() || {};
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  const mounted = useRef(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) return; // Only fetch if user is authenticated to prevent 401 on landing page
    try {
      const res = await axios.get(`${API}/notifications/pending`, { withCredentials: true });
      if (mounted.current && res.data.notifications && res.data.notifications.length > 0) {
        setNotifications(res.data.notifications);
        setCurrentNotification(res.data.notifications[0]);
      }
    } catch (error) {
      // Silently fail if session expired
    }
  }, [user]);

  useEffect(() => {
    mounted.current = true;
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => {
        mounted.current = false;
        clearInterval(interval);
      };
    }
    return () => {
      mounted.current = false;
    };
  }, [user, fetchNotifications]);

  const dismissNotification = async () => {
    if (!currentNotification) return;
    
    try {
      await axios.put(
        `${API}/notifications/${currentNotification.notification_id}/read`,
        {},
        { withCredentials: true }
      );
      
      const remaining = notifications.filter(
        n => n.notification_id !== currentNotification.notification_id
      );
      setNotifications(remaining);
      setCurrentNotification(remaining.length > 0 ? remaining[0] : null);
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  if (!currentNotification || !user) return null;

  const getNotificationStyle = (type) => {
    switch (type) {
      case 'mood_update':
        return 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white';
      case 'brownie_awarded':
        return 'bg-gradient-to-r from-amber-500 to-orange-500 text-white';
      case 'brownie_redeemed':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      case 'reminder_alert':
        return 'bg-gradient-to-r from-pink-500 to-rose-500 text-white';
      case 'conflict_initiated':
      case 'conflict_turn':
        return 'bg-gradient-to-r from-rose-500 to-red-500 text-white';
      default:
        return 'bg-gradient-to-r from-pink-500 to-purple-500 text-white';
    }
  };

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 transform ${getNotificationStyle(currentNotification.type)}`}>
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center flex-1 min-w-0">
            <span className="flex p-2 rounded-lg bg-white/20 mr-3">
              {currentNotification.type?.includes('brownie') ? (
                <Heart className="h-5 w-5 text-white" />
              ) : (
                <AlertCircle className="h-5 w-5 text-white" />
              )}
            </span>
            <div className="truncate">
              <p className="font-medium text-sm sm:text-base">
                {currentNotification.title}
              </p>
              <p className="text-xs sm:text-sm text-white/80 truncate">
                {currentNotification.body}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={dismissNotification}
              className="text-white hover:bg-white/20 p-1.5 h-auto rounded-full"
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationBanner;
